import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../api/notification.api';
import { useAppSelector } from '../store/hooks';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
} from '@mui/material';
import { Send, Notifications, CheckCircle, Schedule } from '@mui/icons-material';

interface Squad {
  id: string;
  name: string;
  skillLevel?: string;
  ageGroup?: string;
}

interface SentNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export const NotificationCenterPage = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState(0);
  const [audience, setAudience] = useState<'all' | 'squad' | 'role'>('all');
  const [formData, setFormData] = useState({
    type: 'info' as 'info' | 'success' | 'warning' | 'error',
    title: '',
    message: '',
    link: '',
  });
  const [selectedSquad, setSelectedSquad] = useState<Squad | null>(null);
  const [selectedRole, setSelectedRole] = useState('PLAYER');
  const [squads, setSquads] = useState<Squad[]>([]);
  const [sentNotifications, setSentNotifications] = useState<SentNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSquads, setLoadingSquads] = useState(true);
  const [loadingSent, setLoadingSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchSquads();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchSentNotifications();
    }
  }, [activeTab, page]);

  const fetchSquads = async () => {
    try {
      setLoadingSquads(true);
      const { default: apiClient } = await import('../api/client');
      const response = await apiClient.get('/squads');
      setSquads(response.data.data || []);
    } catch (err) {
      console.error('Failed to load squads:', err);
    } finally {
      setLoadingSquads(false);
    }
  };

  const fetchSentNotifications = async () => {
    try {
      setLoadingSent(true);
      const response = await notificationAPI.getSentNotifications(page, 20);
      setSentNotifications(response.data || []);
      setTotalPages(response.meta?.totalPages || 1);
    } catch (err) {
      console.error('Failed to load sent notifications:', err);
    } finally {
      setLoadingSent(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim() || !formData.message.trim()) {
      setError('Title and message are required');
      return;
    }

    if (audience === 'squad' && !selectedSquad) {
      setError('Please select a squad');
      return;
    }

    setLoading(true);

    try {
      if (audience === 'all') {
        await notificationAPI.sendToAllUsers(formData);
        setSuccess('Notification sent to all users successfully!');
      } else if (audience === 'squad' && selectedSquad) {
        await notificationAPI.sendToSquad({
          ...formData,
          squadId: selectedSquad.id,
        });
        setSuccess(`Notification sent to squad "${selectedSquad.name}" successfully!`);
      } else if (audience === 'role') {
        await notificationAPI.sendToRole({
          ...formData,
          role: selectedRole,
        });
        setSuccess(`Notification sent to all ${selectedRole}s successfully!`);
      }

      // Reset form
      setFormData({
        type: 'info',
        title: '',
        message: '',
        link: '',
      });
      setSelectedSquad(null);

      // Switch to sent tab after sending
      setTimeout(() => {
        setActiveTab(1);
      }, 1500);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Failed to send notification');
      } else {
        setError('Failed to send notification');
      }
    } finally {
      setLoading(false);
    }
  };

  const getAudienceDescription = () => {
    if (audience === 'all') return 'All active users will receive this notification';
    if (audience === 'squad' && selectedSquad)
      return `All active players in squad "${selectedSquad.name}" will receive this notification`;
    if (audience === 'squad') return 'Select a squad to send notification to its members';
    if (audience === 'role') return `All active users with role ${selectedRole} will receive this notification`;
    return '';
  };

  const canSendToAll = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH';

  const getNotificationTypeColor = (type: string) => {
    const colors: Record<string, 'info' | 'success' | 'warning' | 'error'> = {
      info: 'info',
      success: 'success',
      warning: 'warning',
      error: 'error',
    };
    return colors[type] || 'default';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Notifications sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4">Notification Center</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Send Notification" icon={<Send />} iconPosition="start" />
          <Tab label="Sent Notifications" icon={<CheckCircle />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ p: 3 }} component="form" onSubmit={handleSubmit}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Audience</FormLabel>
              <RadioGroup
                value={audience}
                onChange={(e) => setAudience(e.target.value as 'all' | 'squad' | 'role')}
              >
                {canSendToAll && (
                  <FormControlLabel value="all" control={<Radio />} label="All Users" />
                )}
                <FormControlLabel value="squad" control={<Radio />} label="Specific Squad" />
                {canSendToAll && (
                  <FormControlLabel value="role" control={<Radio />} label="By Role" />
                )}
              </RadioGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {getAudienceDescription()}
              </Typography>
            </FormControl>

            {audience === 'squad' && (
              <Autocomplete
                options={squads}
                getOptionLabel={(option) => `${option.name}${option.skillLevel ? ` (${option.skillLevel})` : ''}`}
                value={selectedSquad}
                onChange={(_, newValue) => setSelectedSquad(newValue)}
                loading={loadingSquads}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Squad"
                    required
                    sx={{ mb: 3 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSquads ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            {audience === 'role' && (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Role</InputLabel>
                <Select
                  value={selectedRole}
                  label="Select Role"
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <MenuItem value="PLAYER">Players</MenuItem>
                  <MenuItem value="PARENT">Parents</MenuItem>
                  <MenuItem value="COACH">Coaches</MenuItem>
                  <MenuItem value="HEAD_COACH">Head Coaches</MenuItem>
                  <MenuItem value="ADMIN">Admins</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Notification Type</InputLabel>
              <Select
                value={formData.type}
                label="Notification Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              >
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 100 }}
              helperText={`${formData.title.length}/100 characters`}
            />

            <TextField
              fullWidth
              label="Message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              multiline
              rows={4}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.message.length}/500 characters`}
            />

            <TextField
              fullWidth
              label="Link (optional)"
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
              sx={{ mb: 3 }}
              helperText="URL to navigate when notification is clicked"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Send />}
                disabled={loading}
                size="large"
              >
                {loading ? 'Sending...' : 'Send Notification'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                disabled={loading}
                size="large"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {loadingSent ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : sentNotifications.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No sent notifications yet
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Recipient</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sentNotifications.map((notification) => (
                        <TableRow key={notification.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {notification.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.message.substring(0, 50)}
                              {notification.message.length > 50 ? '...' : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {notification.user.firstName} {notification.user.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.user.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={notification.type.toUpperCase()}
                              color={getNotificationTypeColor(notification.type)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {notification.isRead ? (
                              <Chip
                                icon={<CheckCircle />}
                                label={`Read ${new Date(notification.readAt!).toLocaleString()}`}
                                color="success"
                                size="small"
                              />
                            ) : (
                              <Chip
                                icon={<Schedule />}
                                label="Unread"
                                color="default"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {new Date(notification.createdAt).toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};
